/**
 * Sandbox **runtime/store** helpers: requests, certificates, IOCs, geo, tags, `flattenSignalGroups`, …
 * For **`full_report` / `resources` JSON shaping** (`groupResourcesByCategory`, `mergeFullReportRootIntoGroupedResources`), see `sandboxReport.ts`.
 */
import dayjs from 'dayjs';
import type {
    Certificate,
    FileResponseInterface,
    GeoData,
    ParsedReport,
    PhishDetection,
    RenderResult,
    Report,
    ReportData,
    Request,
    Resource,
    SandboxData,
    SandboxReport,
    SandboxResources,
    SandboxResponseInterface,
    SandboxResultItem,
    SandboxResultsInterface,
    SandboxSignalGroup,
} from '../../services/interface';
import type { AnalysisStatus } from '../analysis/analysisPhase';

/** `scan_result_i` value the backend uses for a sandbox analysis that is still running. */
export const SANDBOX_SCAN_RESULT_IN_PROGRESS = 255;

/**
 * True while the sandbox analysis is still running, for both shapes that reach the UI: the polled
 * `/v4/sandbox` response (`scan_in_progress`) and the `sandbox_results` embedded in a file lookup
 * (`scan_result_i`). Callers use it to keep polling and to withhold `store_at`, whose report is only
 * complete once the analysis is — `useSandboxReport` caches it immutably, so a partial fetch sticks.
 */
export const isSandboxInProgress = (data?: Pick<SandboxResultsInterface, 'scan_in_progress' | 'scan_result_i'>) =>
    Boolean(data?.scan_in_progress) || data?.scan_result_i === SANDBOX_SCAN_RESULT_IN_PROGRESS;

/**
 * `waiting` lasts only until the first response lands — after that something IS running, so the cards
 * read "Analyzing…" rather than claiming they are still waiting to start.
 *
 * A finished file scan with no sandbox id means nothing is coming: the cards fall back to their
 * no-data state instead of analyzing forever. `sandboxError` already folds in the "this file was
 * never sandboxed" heuristic (`additional_info` without 'sandbox'), so it decides `none` on its own.
 *
 * An Aether scan never exposes a sandbox id — the inline `sandbox_results` block is the whole sandbox.
 * Reading "no id" as "no sandbox" there dropped the cards to `none` the moment the file settled, i.e.
 * to their no-data placeholder for the seconds between the verdict and the report it points at, after
 * which the rows popped in (MDCI-972). The inline block counts as a sandbox while it can still deliver:
 * running, or done with a report to fetch. A verdict with nothing behind it is `none`, as before.
 */
export const getSandboxStatus = ({
    fileData,
    sandboxData,
    sandboxError,
    sandboxId,
}: {
    fileData?: Pick<FileResponseInterface, 'scan_results'>;
    sandboxData?: Pick<SandboxResultsInterface, 'scan_in_progress' | 'scan_result_i' | 'store_at' | 'full_report'>;
    sandboxError?: boolean;
    sandboxId?: string | null;
}): AnalysisStatus => {
    if (sandboxError) {
        return 'none';
    }

    // Nothing has been answered yet — the only point at which "Waiting to start…" is true.
    if (!fileData) {
        return 'waiting';
    }

    /* `store_at` is what `useSandboxReport` fetches; `useSandboxData` withholds it until the file scan
       completes, so the report links stand in for it during that last stretch — either proves a report
       is coming. */
    const hasReportToFetch =
        Boolean(sandboxData?.store_at) ||
        (sandboxData?.full_report != null && Object.keys(sandboxData.full_report).length > 0);
    const hasInlineSandbox =
        sandboxData?.scan_result_i != null && (isSandboxInProgress(sandboxData) || hasReportToFetch);

    if (!sandboxId && !hasInlineSandbox) {
        const isFileScanRunning = fileData.scan_results?.scan_all_result_i === SANDBOX_SCAN_RESULT_IN_PROGRESS;
        return isFileScanRunning ? 'analyzing' : 'none';
    }

    return isSandboxInProgress(sandboxData) ? 'analyzing' : 'done';
};

/**
 * The same question for the URL / IP / domain flow, which has no file scan to read: there the sandbox
 * response is the only thing that reports progress. `none` covers everything that will never produce
 * results — an error or aborted run, an IP/domain scan with no sandbox at all, or a report that came
 * back empty — so the cards keep their own no-data state instead of analyzing forever.
 */
export const getUrlSandboxStatus = ({
    sandboxError,
    sandboxId,
    sandboxResponse,
    isEmptyFullReport,
}: {
    sandboxError?: boolean;
    sandboxId?: string | null;
    sandboxResponse?: Pick<SandboxResultsInterface, 'scan_in_progress' | 'scan_result_i'>;
    isEmptyFullReport?: boolean;
}): AnalysisStatus => {
    if (sandboxError || !sandboxId || isEmptyFullReport) {
        return 'none';
    }

    if (!sandboxResponse) {
        return 'waiting';
    }

    return isSandboxInProgress(sandboxResponse) ? 'analyzing' : 'done';
};

/** SWR `refreshInterval` callback while sandbox metadata is in flight (file + URL flows). */
export const sandboxRefreshInterval = (data?: SandboxResponseInterface) => {
    if (data?.scan_in_progress) {
        return 10000;
    }

    if (
        data?.final_verdict ||
        data?.scan_results?.progress_percentage === 100 ||
        data?.scan_results?.scan_all_result_a === 'Aborted'
    ) {
        return 0;
    }

    return 3000;
};

/** Reads verdict from URL `signal_groups` (string) or file reports (nested object). */
export const getSandboxSignalGroupVerdictString = (group: { verdict?: unknown }): string => {
    const v = group?.verdict;
    if (typeof v === 'string') {
        return v;
    }
    if (v && typeof v === 'object' && v !== null && 'verdict' in v) {
        const inner = (v as { verdict?: unknown }).verdict;
        if (typeof inner === 'string') {
            return inner;
        }
    }
    return 'Unknown';
};

/**
 * Flattens every resource of a sandbox report into one list, and is the entry point for most of the
 * helpers below (certificates, requests, IOCs, …).
 *
 * WHY two branches: the same logical report reaches the UI two ways. `/v4/sandbox/{id}` returns a
 * `reports` map keyed by report id, each with its own `resources` map (a URL scan can emulate several
 * resources); the store JSON fetched from `store_at` instead carries `full_report` as a JSON **string**
 * with a single top-level `resources` map. Both are objects keyed by id, never arrays, hence
 * `Object.values` rather than iteration. A malformed `full_report` yields `[]` instead of throwing —
 * a partial report should degrade to an empty section, not blank the whole details page.
 */
export const getResources = (data: ReportData): Resource[] => {
    if (data?.reports && typeof data.reports === 'object') {
        const reportList = Object.values(data.reports);
        const resourceList = reportList.reduce((acc: Resource[], report: Report) => {
            if (report?.resources) {
                return [...acc, ...Object.values(report.resources)];
            }
            return acc;
        }, []);
        return resourceList;
    } else if (data?.full_report) {
        try {
            const parsedReport = JSON.parse(data.full_report) as ParsedReport;
            if (parsedReport?.resources && typeof parsedReport.resources === 'object') {
                return Object.values(parsedReport.resources);
            }
        } catch {
            return [];
        }
    }
    return [];
};

/** Both `{ renderResults: RenderResult[] }` and a bare `RenderResult[]` appear in sandbox store JSON. */
const getRenderResultsFromResource = (resource: Resource): RenderResult[] => {
    const rr = resource?.renderResults;
    if (Array.isArray(rr)) {
        return rr;
    }
    if (rr && typeof rr === 'object' && Array.isArray((rr as { renderResults?: RenderResult[] }).renderResults)) {
        return (rr as { renderResults: RenderResult[] }).renderResults;
    }
    return [];
};

export const getRenderResults = (data: ReportData): RenderResult[] => {
    const resourceList = getResources(data);
    return resourceList.flatMap((resource) => getRenderResultsFromResource(resource));
};

export const getCertificates = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const seen = new Set<string>();
    const certificateData = renderResultsList.reduce(
        (
            acc: { subjectName: string; issuer: string; validFrom: string; validTo: string }[],
            renderResults: RenderResult,
        ) => {
            const certificates = renderResults?.urlRenderData?.result?.lists?.certificates;
            if (!Array.isArray(certificates)) {
                return acc;
            }
            certificates.forEach((certificate: Certificate) => {
                const dedupeKey = `${certificate.subjectName}||${certificate.issuer}||${certificate.validFrom}||${certificate.validTo}`;
                if (seen.has(dedupeKey)) {
                    return;
                }
                seen.add(dedupeKey);
                acc.push({
                    subjectName: certificate.subjectName,
                    issuer: certificate.issuer,
                    validFrom: dayjs(certificate.validFrom * 1000).format('D MMM YYYY HH:mm'),
                    validTo: dayjs(certificate.validTo * 1000).format('D MMM YYYY HH:mm'),
                });
            });
            return acc;
        },
        [],
    );
    return certificateData;
};

const getRequestDedupeKey = (request: Request): string => {
    const req = isRecord(request.request) ? request.request : undefined;
    const innerReq = req && isRecord(req.request) ? req.request : undefined;
    const requestId = req && typeof req.requestId === 'string' ? req.requestId : '';
    const url = innerReq && typeof innerReq.url === 'string' ? innerReq.url : '';
    const method = innerReq && typeof innerReq.method === 'string' ? innerReq.method : '';
    const timestamp = req && typeof req.timestamp === 'number' ? String(req.timestamp) : '';
    if (!requestId && !url && !method && !timestamp) {
        try {
            return JSON.stringify(request);
        } catch {
            return '';
        }
    }
    return `${requestId}||${url}||${method}||${timestamp}`;
};

export const getRequests = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const seen = new Set<string>();
    const requestList = renderResultsList.reduce((acc: Request[], renderResults: RenderResult) => {
        const requests = renderResults?.urlRenderData?.result?.data?.requests;

        if (requests && typeof requests === 'object') {
            Object.values(requests).forEach((request: Request) => {
                const dedupeKey = getRequestDedupeKey(request);
                if (seen.has(dedupeKey)) {
                    return;
                }
                seen.add(dedupeKey);
                acc.push(request);
            });
        }

        return acc;
    }, []);

    return requestList;
};

export const getPhishDetections = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const phishDetectionList = renderResultsList.reduce((acc: PhishDetection[], renderResults: RenderResult) => {
        const phishDetection = renderResults?.urlRenderData?.result?.phishDetection;
        const phishDetectionState = renderResults?.urlRenderData?.phishDetectionState;

        if (phishDetection && Object.keys(phishDetection).length > 0 && typeof phishDetectionState === 'string') {
            return [...acc, { ...phishDetection, phishDetectionState }];
        }

        return [...acc];
    }, []);

    return phishDetectionList;
};

export const getImages = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const imageList = renderResultsList.reduce((acc: string[], renderResults: RenderResult) => {
        const base64 = renderResults?.base64;

        if (base64) {
            return [...acc, base64];
        }

        return [...acc];
    }, []);

    return imageList;
};

/**
 * Returns the report "root" objects regardless of source shape:
 * - MetaDefender sandbox: a `full_report` (JSON string or object) at the root.
 * - Filescan.IO: a `reports` map whose values are the report roots (no `full_report`).
 * Both expose the same fields (resources, allSignalGroups, iocs, …) so downstream readers
 * can treat them uniformly.
 */
export const getSandboxReportRoots = (sandboxData?: SandboxData | SandboxReport): Record<string, unknown>[] => {
    if (!isRecord(sandboxData)) {
        return [];
    }

    const roots: Record<string, unknown>[] = [];

    const reports = Reflect.get(sandboxData, 'reports');
    if (isRecord(reports)) {
        for (const report of Object.values(reports)) {
            if (isRecord(report)) {
                roots.push(report);
            }
        }
    }

    const fullReport = Reflect.get(sandboxData, 'full_report');
    if (fullReport) {
        let parsed: unknown = fullReport;
        if (typeof fullReport === 'string') {
            try {
                parsed = JSON.parse(fullReport);
            } catch {
                parsed = undefined;
            }
        }
        if (isRecord(parsed)) {
            roots.push(parsed);
        }
    }

    return roots;
};

export const flattenSignalGroups = (sandboxData?: SandboxData | SandboxReport): SandboxSignalGroup[] => {
    const roots = getSandboxReportRoots(sandboxData);
    if (roots.length === 0) {
        return [];
    }

    const allSignalGroups: SandboxSignalGroup[] = [];

    for (const root of roots) {
        const aggregated = Reflect.get(root, 'allSignalGroups');
        const snake = Reflect.get(root, 'signal_groups');
        const aggregatedList = Array.isArray(aggregated) ? (aggregated as SandboxSignalGroup[]) : [];
        const snakeList = Array.isArray(snake) ? (snake as SandboxSignalGroup[]) : [];

        if (aggregatedList.length > 0 || snakeList.length > 0) {
            allSignalGroups.push(...aggregatedList, ...snakeList);
            continue;
        }

        const resources = Reflect.get(root, 'resources');
        if (isRecord(resources)) {
            for (const resource of Object.values(resources)) {
                const signalGroups = isRecord(resource) ? Reflect.get(resource, 'signalGroups') : undefined;
                if (Array.isArray(signalGroups)) {
                    allSignalGroups.push(...(signalGroups as SandboxSignalGroup[]));
                }
            }
        }
    }

    return allSignalGroups.filter(Boolean);
};

export const getTotalThreatIndicatorsFromResources = (sandboxData?: SandboxData | SandboxReport): number => {
    return flattenSignalGroups(sandboxData).length;
};

const iocTypeMap: Record<string, string> = {
    url: 'URL',
    ip: 'IP',
    domain: 'Domain',
    email: 'Email',
    md5: 'MD5',
    sha1: 'SHA1',
    sha256: 'SHA-256',
    sha512: 'SHA-512',
    uuid: 'UUID',
    registry_path: 'Registry Pathway',
    revision_save_id: 'Revision Save ID',
    btc_wallet: 'BTC Wallet',
    eth_wallet: 'ETH Wallet',
    xmr_wallet: 'XMR Wallet',
    unc_path: 'UNC Path',
    uncategorized: 'Uncategorized',
};

const formatFallbackIocType = (typeKey: string) =>
    typeKey
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

export const getIocType = (typeKey: string) => iocTypeMap[typeKey] || formatFallbackIocType(typeKey);

export const getIocValue = (item: unknown): string => {
    if (typeof item === 'string') {
        return item;
    }
    if (!isRecord(item)) {
        return '';
    }
    const possibleValue =
        item.value ??
        item.data ??
        item.ioc ??
        item.indicator ??
        item.str ??
        item.hash ??
        item.url ??
        item.uri ??
        item.ip ??
        item.domain ??
        item.host ??
        item.email ??
        item.path ??
        item.address ??
        item.wallet;
    return typeof possibleValue === 'string' ? possibleValue : '';
};

export interface SandboxIoc {
    type: string;
    value: string;
    verdict: string;
    origin: unknown;
}

/**
 * Filescan.IO reports expose IoCs per resource as `extracted<Type>` arrays instead of a top-level
 * `iocs` map. Map each field to the same type key the MetaDefender `iocs` map uses.
 */
const EXTRACTED_IOC_FIELDS: Record<string, string> = {
    extractedUrls: 'url',
    extractedIPs: 'ip',
    extractedDomains: 'domain',
    extractedEmails: 'email',
    extractedHashesMD5: 'md5',
    extractedHashesSHA1: 'sha1',
    extractedHashesSHA256: 'sha256',
    extractedHashesSHA512: 'sha512',
    extractedUUIDs: 'uuid',
    extractedRegistryPathways: 'registry_path',
    extractedRevisionSaveIDs: 'revision_save_id',
    extractedBTCWallets: 'btc_wallet',
    extractedETHWallets: 'eth_wallet',
    extractedXMRWallets: 'xmr_wallet',
    extractedUNCPaths: 'unc_path',
    extractedUncategorizedResources: 'uncategorized',
};

/**
 * Extracts IoCs from either report shape, de-duplicated by type + value:
 * - MetaDefender: a top-level `iocs` map (`{ ip: [...], url: [...] }`).
 * - Filescan.IO: per-resource `extracted<Type>` arrays under `resources[id]`.
 */
export const extractSandboxIocs = (sandboxReport?: SandboxData): SandboxIoc[] => {
    const roots = getSandboxReportRoots(sandboxReport);
    const seen = new Set<string>();
    const iocs: SandboxIoc[] = [];

    const pushIoc = (type: string, value: string, verdict: string, origin: unknown) => {
        if (!value) {
            return;
        }
        const dedupeKey = `${type}||${value}`;
        if (seen.has(dedupeKey)) {
            return;
        }
        seen.add(dedupeKey);
        iocs.push({ type, value, verdict, origin });
    };

    const addIoc = (typeKey: string, item: unknown) => {
        const type = getIocType(typeKey);
        const origin = getIocOrigin(item);

        // Filescan extracted items hold their value(s) under `references[].data`, not on the item itself.
        const references = isRecord(item) ? Reflect.get(item, 'references') : undefined;
        if (Array.isArray(references) && references.length > 0) {
            for (const reference of references) {
                const data = isRecord(reference) ? reference.data : undefined;
                const value = typeof data === 'string' ? data : getIocValue(reference);
                pushIoc(type, value, getIocVerdict(reference) || getIocVerdict(item), origin);
            }
            return;
        }

        pushIoc(type, getIocValue(item), getIocVerdict(item), origin);
    };

    for (const root of roots) {
        const rootIocs = Reflect.get(root, 'iocs');
        if (isRecord(rootIocs)) {
            for (const [typeKey, typeValues] of Object.entries(rootIocs)) {
                if (Array.isArray(typeValues)) {
                    typeValues.forEach((item) => addIoc(typeKey, item));
                }
            }
        }

        const resources = Reflect.get(root, 'resources');
        if (isRecord(resources)) {
            for (const resource of Object.values(resources)) {
                if (!isRecord(resource)) {
                    continue;
                }
                for (const [field, typeKey] of Object.entries(EXTRACTED_IOC_FIELDS)) {
                    const items = Reflect.get(resource, field);
                    if (Array.isArray(items)) {
                        items.forEach((item) => addIoc(typeKey, item));
                    }
                }
            }
        }
    }

    return iocs;
};

export const getGroupedIocsCount = (sandboxReport?: SandboxData): number => extractSandboxIocs(sandboxReport).length;

export const getTotalYaraRulesFromResources = (sandboxResources: SandboxResources): number => {
    if (!sandboxResources?.yaraMatches) {
        return 0;
    }
    return sandboxResources.yaraMatches.reduce(
        (total: number, group) => total + (Array.isArray(group) ? group.length : 0),
        0,
    );
};

/**
 * `emulationData` arrives in two shapes: a flat list of events (single emulated resource) or a list of
 * per-resource event groups (a URL/archive that produced several). Nothing in the payload distinguishes
 * them, so the first element is probed instead. The `as unknown[][]` cast is needed because the declared
 * type is the union of both shapes — after the `Array.isArray(emulationData[0])` probe TypeScript still
 * cannot narrow the array itself, and the reduce only needs each group's length, so the element type is
 * deliberately widened to `unknown` rather than asserted to the event interface.
 */
export const getTotalEmulationEventsFromResources = (sandboxResources: SandboxResources): number => {
    if (!sandboxResources?.emulationData) {
        return 0;
    }
    const emulationData = sandboxResources.emulationData;
    if (Array.isArray(emulationData) && emulationData.length > 0 && !Array.isArray(emulationData[0])) {
        return emulationData.length;
    }
    if (Array.isArray(emulationData) && emulationData.length > 0 && Array.isArray(emulationData[0])) {
        return (emulationData as unknown[][]).reduce(
            (total: number, group) => total + (Array.isArray(group) ? group.length : 0),
            0,
        );
    }
    return 0;
};

export const getTotalExtractedFilesFromResources = (sandboxResources: SandboxResources): number => {
    let total = 0;
    if (sandboxResources?.extractedFiles) {
        total += sandboxResources.extractedFiles.reduce(
            (count: number, group) => count + (Array.isArray(group) ? group.length : 0),
            0,
        );
    }
    if (sandboxResources?.fileDownloadResults) {
        total += sandboxResources.fileDownloadResults.reduce(
            (count: number, group) => count + (Array.isArray(group) ? group.length : 0),
            0,
        );
    }

    return total;
};

export const getTotalExtractedStringsFromResources = (sandboxResources?: SandboxResources): number => {
    if (!sandboxResources) {
        return 0;
    }

    if (!Array.isArray(sandboxResources.strings)) {
        return 0;
    }

    return sandboxResources.strings.reduce((total: number, group) => {
        if (!Array.isArray(group) || group.length === 0) {
            return total;
        }

        const first = group[0];

        if (first && typeof first === 'object' && 'references' in first) {
            const groupTotal = group.reduce((acc, item) => {
                if (item && typeof item === 'object' && 'references' in item) {
                    const maybeReferences = (item as { references?: unknown }).references;
                    if (Array.isArray(maybeReferences)) {
                        return acc + maybeReferences.length;
                    }
                }
                return acc;
            }, 0);

            return total + groupTotal;
        }

        return total + group.length;
    }, 0);
};

export const getGeoDataFromResources = (sandboxResources: SandboxResources): GeoData[] => {
    const results = Array.isArray(sandboxResources?.results) ? sandboxResources.results : [];

    const hasGeoInfo = (
        item: SandboxResultItem,
    ): item is SandboxResultItem & {
        data: {
            geo_info: {
                country: { name: string };
                location: { latitude: number; longitude: number };
            };
        };
    } => {
        const name = item?.data?.geo_info?.country?.name;
        const latitude = item?.data?.geo_info?.location?.latitude;
        const longitude = item?.data?.geo_info?.location?.longitude;
        return typeof name === 'string' && typeof latitude === 'number' && typeof longitude === 'number';
    };

    return results
        .flat()
        .filter(hasGeoInfo)
        .map((item) => {
            const name = item.data.geo_info.country.name;
            const latitude = item.data.geo_info.location.latitude;
            const longitude = item.data.geo_info.location.longitude;
            return {
                country: { name },
                location: { latitude, longitude },
            };
        });
};

export interface GeoTableRow {
    ip: string;
    city?: string;
    countryName?: string;
    // ISO2 (e.g. 'CN') when the geo provider includes it — used for the flag. Absent → no flag.
    countryCode?: string;
    domain?: string;
    verdict?: string;
}

/** Rows for the geolocation table under the map: one per result item carrying geo_info. */
export const getGeoTableRowsFromResources = (sandboxResources: SandboxResources): GeoTableRow[] => {
    const results = Array.isArray(sandboxResources?.results) ? sandboxResources.results : [];

    return results
        .flat()
        .filter((item): item is SandboxResultItem => isRecord(item) && isRecord(item.data?.geo_info))
        .map((item) => {
            const geo = item.data!.geo_info as Record<string, unknown>;
            const country = isRecord(geo.country) ? geo.country : undefined;
            const city = isRecord(geo.city) ? geo.city : undefined;
            const origin = isRecord(item.origin) ? item.origin : undefined;

            const asString = (value: unknown) => (typeof value === 'string' && value ? value : undefined);

            return {
                ip: asString(item.resource) ?? '-',
                city: asString(city?.name),
                countryName: asString(country?.name),
                countryCode: asString(country?.iso_code),
                domain: asString(origin?.identifier),
                verdict: asString(item.verdict),
            };
        });
};

type SandboxTagVerdict = { verdict?: string };
export type SandboxTag = { name: string; verdict?: SandboxTagVerdict };

export const getSandboxTagsFromResources = (sandboxResources: SandboxResources): SandboxTag[] => {
    if (!sandboxResources) {
        return [];
    }

    return Array.from(
        (Array.isArray(sandboxResources?.allTags) ? sandboxResources.allTags : [])
            .flat()
            .map((tagEntry: unknown) => {
                if (tagEntry && typeof tagEntry === 'object' && 'tag' in tagEntry) {
                    return (tagEntry as { tag?: unknown }).tag ?? tagEntry;
                }
                return tagEntry;
            })
            .filter((tag: unknown): tag is SandboxTag => {
                return (
                    !!tag &&
                    typeof tag === 'object' &&
                    'name' in tag &&
                    typeof (tag as { name?: unknown }).name === 'string'
                );
            })
            .reduce((map: Map<string, SandboxTag>, tag: SandboxTag) => {
                if (!map.has(tag.name)) {
                    map.set(tag.name, { name: tag.name, verdict: (tag as { verdict?: SandboxTagVerdict }).verdict });
                }
                return map;
            }, new Map<string, SandboxTag>())
            .values(),
    );
};

export interface IoCData {
    type: string;
    name: string;
    value: string;
    properties?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

/** Normalize sandbox IOC `origin` objects (shape varies by API version). */
const originRecordToDisplayString = (origin: unknown): string => {
    if (typeof origin === 'string') {
        return origin;
    }
    if (!isRecord(origin)) {
        return '';
    }
    if (typeof origin.type === 'string') {
        return origin.type;
    }
    if (typeof origin.name === 'string') {
        return origin.name;
    }
    if (typeof origin.source === 'string') {
        return origin.source;
    }
    if (typeof origin.label === 'string') {
        return origin.label;
    }
    if (typeof origin.identifier === 'string' && origin.identifier.length > 0) {
        return origin.identifier;
    }
    return '';
};

const originsArrayToDisplayString = (origins: unknown): string => {
    if (!Array.isArray(origins) || origins.length === 0) {
        return '';
    }
    const parts = origins
        .map((entry) => originRecordToDisplayString(entry))
        .filter((part): part is string => part.length > 0);
    const seen = new Set<string>();
    const unique = parts.filter((part) => {
        if (seen.has(part)) {
            return false;
        }
        seen.add(part);
        return true;
    });
    return unique.join(', ');
};

export const getIocOrigin = (item: unknown): string => {
    if (!isRecord(item)) {
        return '';
    }

    const fromTopLevelOrigin = originRecordToDisplayString(item.origin);
    if (fromTopLevelOrigin) {
        return fromTopLevelOrigin;
    }

    const fromOriginsArray = originsArrayToDisplayString(item.origins);
    if (fromOriginsArray) {
        return fromOriginsArray;
    }

    if (typeof item.source === 'string') {
        return item.source;
    }

    if (typeof item.sourceIdentifier === 'string') {
        return item.sourceIdentifier;
    }

    if (isRecord(item.metadata)) {
        const meta = item.metadata;
        const fromMetaOrigin = originRecordToDisplayString(meta.origin);
        if (fromMetaOrigin) {
            return fromMetaOrigin;
        }
        if (typeof meta.source === 'string') {
            return meta.source;
        }
    }

    return '';
};

export const getIocVerdict = (item: unknown): string => {
    if (!isRecord(item)) {
        return '';
    }

    if (typeof item.verdict === 'string') {
        return item.verdict;
    }

    if (isRecord(item.metadata) && typeof item.metadata.verdict === 'string') {
        return item.metadata.verdict;
    }

    return '';
};

export interface SandboxFullReportExportOption {
    key: string;
    label: string;
    icon: string;
    url: string;
}

/** `full_report` link object from `/v4/sandbox/:id` (and compatible payloads). */
export type SandboxFullReportLinks = {
    pdf?: string;
    html?: string;
    json?: string;
};

export const getSandboxFullReportExportOptions = (
    fullReport: SandboxFullReportLinks | null | undefined,
    t: (key: string) => string,
): SandboxFullReportExportOption[] => {
    if (!fullReport) {
        return [];
    }

    const options: SandboxFullReportExportOption[] = [];

    if (fullReport.pdf) {
        options.push({ key: 'pdf', label: t('PDF'), icon: 'report', url: fullReport.pdf });
    }

    if (fullReport.html) {
        options.push({ key: 'html', label: t('HTML'), icon: 'checklist', url: fullReport.html });
    }

    if (fullReport.json) {
        options.push({ key: 'json', label: t('JSON'), icon: 'base', url: fullReport.json });
    }

    return options;
};

export const openSandboxFullReportDownload = (url?: string): void => {
    if (!url) {
        return;
    }
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

export const getSandboxReportDownloadHoverHint = (t: (key: string) => string): string =>
    t('In order to download the Sandbox Report, please enable Sandbox Analysis');
